import { afterNextRender, inject, Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { ChatActions } from 'libs/shared/data/stores/chat/chat.actions';
import { BehaviorSubject } from 'rxjs';

import { PostMessageTemplateRequest } from '@offconon/core-api';

@Injectable({
  providedIn: 'root',
})
export class WebSocketService implements OnDestroy {
  private worker: Worker;
  private messageSubject = new BehaviorSubject<any>(null);
  private connectionStatusSubject = new BehaviorSubject<boolean>(false);
  private lastPongTime = 0;
  private checkConnectionInterval: any;
  private currentUrl: string | null = null;
  private maxReconnectAttempts = 5;
  private reconnectAttempts = 0;
  private reconnectTimeout: any;
  private reconnectDelay = 3000; // Start with 3 seconds
  private store = inject(Store);
  constructor() {
    afterNextRender(() => {
      try {
        this.worker = new Worker(
          new URL(
            '../../../../../../libs/shared/utils/services/src/lib/websocket.worker',
            import.meta.url,
          ),
          {
            type: 'module',
          },
        );

        this.worker.onmessage = (event) => {
          const { type, payload } = event.data;
          switch (type) {
            case 'message':
              if (payload.communication_type === 'ping') {
                break;
              }

              switch (payload.communication_type) {
                case 'chat':
                  this.store.dispatch(ChatActions.addMessage({ message: payload }));
                  this.messageSubject.next(payload);
                  break;
                default:
                  break;
              }
              break;
            case 'open':
              this.connectionStatusSubject.next(true);
              break;
            case 'close':
              this.connectionStatusSubject.next(false);
              break;
            case 'pong':
              this.lastPongTime = Date.now();
              break;
            case 'state':
              if (payload.readyState === WebSocket.OPEN) {
                this.connectionStatusSubject.next(true);
              } else {
                this.connectionStatusSubject.next(false);
              }
              break;
          }
        };
      } catch (error) {
        console.error('Failed to create Web Worker:', error);
      }
    });
  }

  connect(url: string) {
    this.currentUrl = url;
    this.reconnectAttempts = 0;
    const token = JSON.parse(localStorage.getItem('auth') || '').user?.jwt_token;
    if (token) {
      this.worker?.postMessage({ type: 'connect', payload: { url, token } });
    }
  }

  send(message: PostMessageTemplateRequest) {
    this.worker?.postMessage({ type: 'send', payload: { message: JSON.stringify(message) } });
  }

  close() {
    this.worker?.postMessage({ type: 'close' });
  }

  onMessage() {
    return this.messageSubject.asObservable();
  }

  onConnectionStatus() {
    return this.connectionStatusSubject.asObservable();
  }

  private reconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.connectionStatusSubject.next(false);
      return;
    }

    if (!this.currentUrl) {
      console.error('No URL to reconnect to');
      return;
    }

    // Clear any existing reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    // Exponential backoff: delay increases with each attempt
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect(this.currentUrl!);
    }, delay);
  }

  ngOnDestroy() {
    if (this.checkConnectionInterval) {
      clearInterval(this.checkConnectionInterval);
    }
    this.close();
  }
}
